import { useQuery as useApolloQuery } from '@apollo/client/react';
import { CrmConnection, Organization } from '../../../types/organization.types';
import { GET_ORGANIZATION } from '../../../lib/graphql/queries/organizations';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';

// --- GQL raw types ---

export interface GQLAgentSummary {
  agentId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: string | null;
  status: string | null;
  photoUrl: string | null;
}

export interface GQLUser {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  photoUrl: string | null;
  role: string;
  status: string;
  isActive: boolean;
  invitedBy: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  availableForCalls: boolean | null;
  availableForTransfers: boolean | null;
  createdAt: string | null;
}

export interface GQLStatusCount {
  label: string;
  count: number;
}

export interface GQLOrganizationStats {
  totalContacts: number;
  totalCalls: number;
  totalEmails: number;
  totalSms: number;
  totalNotes: number;
  totalSearchAlertsContacts: number;
  totalSearchAlertsCreatedByRemi: number;
  totalSearchesMadeByUsers: number;
  totalPageViews: number;
  totalPropertyViews: number;
  totalSearchPageViews: number;
  totalHomeValuations: number;
  totalSavedProperties: number;
  totalEmailPropertyLinkViews: number;
  totalWebsiteActivity: number;
  totalEmailOpens: number;
  totalEmailClicks: number;
  uniqueContactsCallsAnswered: number;
  uniqueContactsEmailsOpened: number;
  uniqueContactsEmailsClicked: number;
  uniqueContactsPropertyClicked: number;
  uniqueContactsSearching: number;
  uniqueContactsWithWebsiteActivity: number;
  uniqueContactsWithConversions: number;
  uniqueActiveUsers: number;
  totalClientTouches: number;
  totalClientTouchesCalls: number;
  totalClientTouchesEmails: number;
  totalClientTouchesWebsiteActivity: number;
  totalClientTouchesConversions: number;
  clientsInCallNurture: number;
  clientsInEmailNurture: number;
  contactsUpdatedToday: number;
  eventsProcessedToday: number;
  contactsByStatus: GQLStatusCount[];
  contactsByLifecycleStage: GQLStatusCount[];
  totalLiveTransfers: number;
}

export interface GQLEmailConfig {
  organizationId: string;
  sendgridSenderId: string | null;
  sendgridSenderEmail: string | null;
  sendgridDomainId: string | null;
  sendgridReplyToEmail: string | null;
  sendgridReplyToName: string | null;
  sendgridFromName: string | null;
  emailSetup: string | null;
  requiredDnsRecords: string | null;
  dnsValidatedAt: string | null;
  emailSetupDate: string | null;
  emailMethod: string | null;
  senderPrefix: string | null;
  dnsValidationDetails: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface GQLFlowSummary {
  organizationId: string;
  flowId: string;
  name: string;
  description: string | null;
  flowType: string | null;
  subType: string | null;
  maxAttempts: number | null;
  rejoin: boolean | null;
  status: string | null;
  scheduledFor: string | null;
  workflowType: string | null;
  timezone: string | null;
  allowedHours: string | null;
  allowedDays: string | null;
  excludeHolidays: boolean | null;
  countryCode: string | null;
  cadence: number | null;
  scheduleOverUnit: string | null;
  schedulingWindow: number | null;
  promptParameters: string | null;
  descriptionId: string | null;
  transferNumbers: string | null;
  useRoundRobin: boolean | null;
  inbound: boolean | null;
  activeCount: number | null;
  finishedCount: number | null;
  scheduledCount: number | null;
  standardId: string | null;
  gFlow: string | null;
  leadSource: string | null;
  leadEmail: string | null;
  valueLink: string | null;
  voicemail: string | null;
  voicemailContent: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  pauseStartedAt: string | null;
  lastPausedAt: string | null;
  lastResumedAt: string | null;
  lastStatusUpdate: string | null;
}

interface GQLOrganization {
  organizationId: string;
  orgName: string;
  orgPhone: string | null;
  active: boolean;
  completedSetup: boolean;
  acceptedTerms: boolean;
  activeSubscription: boolean | null;
  beta: boolean | null;
  email: string | null;
  notificationEmail: string | null;
  timezone: string | null;
  domain: string | null;
  assistantName: string | null;
  assistantNamePronunciation: string | null;
  clientAddress: string | null;
  primaryContact: string | null;
  subscription: string | null;
  callSettings: string | null;
  phoneNumbers: string | null;
  branding: string | null;
  logoUrl: string | null;
  combinedName: string | null;
  subdomain: string | null;
  calendlyUrl: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  twilioSecretRef: string | null;
  syncLink: string | null;
  stripeAccountId: string | null;
  stripeCustomerId: string | null;
  connectedCrm: string | null;
  isOnboardingComplete: boolean | null;
  activeClient: boolean | null;
  externalClient: boolean | null;
  emailCampaignsEnabled: boolean | null;
  dnsConfigured: boolean | null;
  smsAllOutcomes: boolean | null;
  orgLinks: string | null;
  fubSetup: string | null;
  hubspot: string | null;
  onboardingSetup: string | null;
  onboardingData: string | null;
  settings: string | null;
  sms: string | null;
  rolloutConfig: string | null;
  remiIntroName: string | null;
  cincSetup: string | null;
  oauthSetup: string | null;
  lastInitialSync: string | null;
  databaseUploads: string | null;
  integrationSetupRequests: string | null;
  databaseSize: number | null;
  brokerageName: string | null;
  clientOwner: string | null;
  crmConnections: CrmConnection[] | null;
  users: GQLUser[];
  emailConfig: GQLEmailConfig | null;
}

export interface GetOrganizationData {
  organization: GQLOrganization;
}

// --- Extended type that includes GQL-only data ---

export interface MappedOrganization extends Organization {
  active: boolean;
  twilioSecretRef?: string;
  combinedName?: string;
  calendlyUrl?: string;
  stripeAccountId?: string;
  stripeCustomerId?: string;
  externalClient?: boolean;
  beta?: boolean;
  completedSetup?: boolean;
}

// --- JSON parse helper ---

function tryParseJSON<T>(value: string | null | undefined): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

// --- Mapper ---

export function mapGQLOrganization(o: GQLOrganization): MappedOrganization {
  return {
    // Identity
    id: o.organizationId,
    org_name: o.orgName,
    org_phone: o.orgPhone ?? undefined,
    timezone: o.timezone ?? 'UTC',

    // Booleans
    active: o.active,
    activeClient: o.activeClient ?? undefined,
    active_subscription: o.activeSubscription ?? undefined,
    accepted_terms: o.acceptedTerms,
    isOnboardingComplete: o.isOnboardingComplete ?? undefined,
    emailCampaignsEnabled: o.emailCampaignsEnabled ?? undefined,
    dns_configured: o.dnsConfigured ?? undefined,
    smsAllOutcomes: o.smsAllOutcomes ?? undefined,

    // Simple strings
    email: o.email ?? undefined,
    notification_email: o.notificationEmail ?? undefined,
    domain: o.domain ?? undefined,
    subdomain: o.subdomain ?? undefined,
    logoUrl: o.logoUrl ?? undefined,
    assistant_name: o.assistantName ?? undefined,
    assistant_name_pronunciation: o.assistantNamePronunciation ?? undefined,
    remi_intro_name: o.remiIntroName ?? undefined,
    sync_link: o.syncLink ?? undefined,
    connected_crm: o.connectedCrm ?? undefined,

    // JSON string fields → parsed objects
    client_address: tryParseJSON(o.clientAddress),
    primary_contact: tryParseJSON(o.primaryContact),
    subscription: tryParseJSON(o.subscription),
    call_settings: tryParseJSON(o.callSettings),
    phoneNumbers: tryParseJSON(o.phoneNumbers),
    branding: tryParseJSON(o.branding),
    org_links: tryParseJSON(o.orgLinks),
    fubSetup: tryParseJSON(o.fubSetup),
    hubspot: tryParseJSON(o.hubspot),
    onboardingSetup: tryParseJSON(o.onboardingSetup),
    onboardingData: tryParseJSON(o.onboardingData),
    settings: tryParseJSON(o.settings),
    sms: tryParseJSON(o.sms),
    rollout: tryParseJSON(o.rolloutConfig),

    // New GQL fields
    cincSetup: tryParseJSON(o.cincSetup),
    oauthSetup: tryParseJSON(o.oauthSetup),
    lastInitialSyncAt: o.lastInitialSync ?? undefined,
    databaseUploads: tryParseJSON(o.databaseUploads),
    integrationSetupRequests: tryParseJSON(o.integrationSetupRequests),
    databaseSize: o.databaseSize ?? undefined,
    brokerage_name: o.brokerageName ?? undefined,
    client_owner: o.clientOwner ?? undefined,

    // CRM connections — required by IntegrationsModal to detect FUB/Rollout
    // status and to surface the stages config in Client Admin.
    crmConnections: o.crmConnections ?? undefined,

    // Email config → map to existing fields
    sendgrid_reply_to_email: o.emailConfig?.sendgridReplyToEmail ?? undefined,
    sendgrid_reply_to_name: o.emailConfig?.sendgridReplyToName ?? undefined,
    sendgrid_sender_email: o.emailConfig?.sendgridSenderEmail ?? undefined,
    emailSetup: tryParseJSON(o.emailConfig?.emailSetup),
    required_dns_records: tryParseJSON(o.emailConfig?.requiredDnsRecords),

    // Users (GQL User objects)
    users: o.users,

    // GQL-only scalars
    twilioSecretRef: o.twilioSecretRef ?? undefined,
    combinedName: o.combinedName ?? undefined,
    calendlyUrl: o.calendlyUrl ?? undefined,
    stripeAccountId: o.stripeAccountId ?? undefined,
    stripeCustomerId: o.stripeCustomerId ?? undefined,
    externalClient: o.externalClient ?? undefined,
    beta: o.beta ?? undefined,
    completedSetup: o.completedSetup,
  } as MappedOrganization;
}

// --- Hook ---

export const useOrganization = (organizationId?: string) => {
  const { organization: storeOrg } = useOrganizationStore();
  const orgId = organizationId ?? storeOrg?.id;

  const { data, loading, error, refetch } = useApolloQuery<GetOrganizationData>(
    GET_ORGANIZATION,
    {
      variables: { organizationId: orgId ?? '' },
      skip: !orgId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const organization = data?.organization
    ? mapGQLOrganization(data.organization)
    : null;

  return {
    organization,
    isLoading: loading,
    error,
    refetch,
  };
};
